import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';

interface TodoNode {
  id: string; parent_id: string | null; text: string; done: number;
  assignee_id: string | null; assignee_name: string | null; due_date: string | null;
  sort_order: number; children: TodoNode[];
}
interface Staff { id: string; name: string; }

/**
 * <cadence-event-todos> — realtime collaborative checklist for one event. Reads the
 * tree over REST, subscribes to the event's Durable Object via WebSocket for live
 * updates (any admin's change is pushed to all), and writes via REST (which the server
 * broadcasts). If the socket drops it reconnects, and mutations refetch as a fallback.
 */
@customElement('cadence-event-todos')
export class CadenceEventTodos extends LitElement {
  @property() eventId = '';
  @state() private tree: TodoNode[] = [];
  @state() private staff: Staff[] = [];
  @state() private loading = true;
  @state() private newText = '';
  @state() private addChildFor: string | null = null;
  @state() private childText = '';
  @state() private editingId: string | null = null;
  @state() private editText = '';
  @state() private live = false;

  private ws: WebSocket | null = null;
  private closing = false;
  private reconnectTimer: number | null = null;
  private dragId: string | null = null;
  private readonly canManage = store.hasPermission('events.manage_todos');

  static styles = css`
    :host { display: block; }
    .add { display: flex; gap: 8px; margin-bottom: var(--space-3); }
    .add cadence-input { flex: 1; }
    .live { font-size: 0.75rem; color: var(--color-text-secondary); display: inline-flex; align-items: center; gap: 6px; margin-bottom: var(--space-3); }
    .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--color-text-secondary); }
    .dot.on { background: var(--color-success, #16a34a); }
    .row { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: var(--radius-md); border: 1px solid transparent; }
    .row:hover { background: var(--color-bg-secondary); }
    .row.drag-over { border-color: var(--color-primary); }
    .grip { cursor: grab; color: var(--color-text-secondary); user-select: none; display: inline-flex; flex-shrink: 0; }
    .grip svg { width: 16px; height: 16px; }
    input[type=checkbox] { width: 16px; height: 16px; accent-color: var(--color-primary); cursor: pointer; flex-shrink: 0; }
    .text { flex: 1; cursor: text; min-width: 0; }
    .text.done { text-decoration: line-through; color: var(--color-text-secondary); }
    .editwrap { flex: 1; display: flex; align-items: center; gap: 4px; min-width: 0; }
    .edit { flex: 1; min-width: 0; font: inherit; padding: 4px 8px; border: 1px solid var(--color-primary); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); }
    select, input[type=date] { font: inherit; font-size: 0.8rem; padding: 4px 6px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); }
    .meta { font-size: 0.78rem; color: var(--color-text-secondary); white-space: nowrap; }
    .iconbtn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 2px 6px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .iconbtn svg { width: 17px; height: 17px; }
    .iconbtn:hover { background: var(--color-bg-primary); color: var(--color-primary); }
    .iconbtn.del:hover { color: var(--color-danger); }
    .iconbtn.confirm:hover { color: var(--color-success, #16a34a); }
    .child-add { display: flex; gap: 8px; padding: 4px 0; }
    .child-add cadence-input { flex: 1; }
    .overdue { color: var(--color-danger); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.start(); }
  disconnectedCallback(): void { super.disconnectedCallback(); this.teardown(); }
  updated(c: Map<string, unknown>) { if (c.has('eventId') && this.eventId) this.start(); }

  private start() {
    if (!this.eventId) return;
    this.teardown();
    this.closing = false;
    this.load();
    this.connectWs();
  }

  private teardown() {
    this.closing = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.ws) { try { this.ws.close(); } catch { /* already closed */ } this.ws = null; }
  }

  private async load() {
    this.loading = true;
    try {
      const [tree, staff] = await Promise.all([
        api.get<TodoNode[]>(`/events/${this.eventId}/todos`),
        api.get<any>('/teachers').catch(() => []),
      ]);
      this.tree = tree;
      const list = Array.isArray(staff) ? staff : (staff.teachers ?? []);
      this.staff = list.map((s: any) => ({ id: s.id, name: `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.email }));
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private connectWs() {
    try {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${location.host}/api/v1/events/${this.eventId}/todos/ws`);
      this.ws = ws;
      ws.addEventListener('open', () => { this.live = true; });
      ws.addEventListener('message', (ev) => {
        try {
          const msg = JSON.parse(ev.data as string);
          if (msg.type === 'sync' && Array.isArray(msg.todos)) this.tree = msg.todos;
        } catch { /* ignore malformed frames */ }
      });
      ws.addEventListener('close', () => { this.live = false; this.scheduleReconnect(); });
      ws.addEventListener('error', () => { this.live = false; });
    } catch { this.live = false; this.scheduleReconnect(); }
  }

  private scheduleReconnect() {
    if (this.closing || this.reconnectTimer) return;
    this.reconnectTimer = window.setTimeout(() => { this.reconnectTimer = null; if (!this.closing) this.connectWs(); }, 2500);
  }

  /** When realtime isn't connected, mutations won't be pushed back — refetch instead. */
  private async afterMutation() { if (!this.live) await this.load(); }

  // ---- mutations ----
  private async addTodo(parentId: string | null, text: string) {
    const txt = text.trim();
    if (!txt) return;
    try {
      await api.post(`/events/${this.eventId}/todos`, { text: txt, parent_id: parentId });
      if (parentId) { this.childText = ''; this.addChildFor = null; } else { this.newText = ''; }
      await this.afterMutation();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async patch(id: string, body: Record<string, unknown>) {
    try { await api.put(`/events/${this.eventId}/todos/${id}`, body); await this.afterMutation(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async removeTodo(node: TodoNode) {
    const hasKids = node.children.length > 0;
    if (!confirm(hasKids ? t('events.todos.confirmDeleteWithChildren') : t('events.todos.confirmDelete'))) return;
    try { await api.del(`/events/${this.eventId}/todos/${node.id}`); await this.afterMutation(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private startEdit(node: TodoNode) { if (!this.canManage) return; this.editingId = node.id; this.editText = node.text; }
  private commitEdit() {
    const id = this.editingId; const text = this.editText.trim();
    this.editingId = null;
    if (id && text) this.patch(id, { text });
  }
  private cancelEdit() { this.editingId = null; }

  // ---- drag reorder (within the same sibling group) ----
  private siblingsOf(id: string, nodes = this.tree): TodoNode[] | null {
    if (nodes.some((n) => n.id === id)) return nodes;
    for (const n of nodes) { const found = this.siblingsOf(id, n.children); if (found) return found; }
    return null;
  }
  private onDrop(target: TodoNode) {
    const dragId = this.dragId; this.dragId = null;
    if (!dragId || dragId === target.id) return;
    const sibs = this.siblingsOf(target.id);
    if (!sibs || !sibs.some((n) => n.id === dragId)) return; // only reorder within the same group
    const ordered = sibs.filter((n) => n.id !== dragId);
    const idx = ordered.findIndex((n) => n.id === target.id);
    const before = ordered[idx - 1]?.sort_order;
    const newOrder = before != null ? (before + target.sort_order) / 2 : target.sort_order - 1;
    this.patch(dragId, { sort_order: newOrder });
  }

  private fmtDue(d: string | null) {
    if (!d) return '';
    const overdue = d < new Date().toISOString().slice(0, 10);
    return html`<span class="meta ${overdue ? 'overdue' : ''}">${t('events.todos.due', { date: d })}</span>`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      <div class="live"><span class="dot ${this.live ? 'on' : ''}"></span>${this.live ? t('events.todos.live') : t('events.todos.offline')}</div>
      ${this.canManage ? html`
        <div class="add">
          <cadence-input placeholder=${t('events.todos.addPlaceholder')} .value=${this.newText}
            @input=${(e: CustomEvent) => (this.newText = e.detail)}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.addTodo(null, this.newText); }}></cadence-input>
          <cadence-button @click=${() => this.addTodo(null, this.newText)}>${t('events.actions.add')}</cadence-button>
        </div>` : ''}
      ${this.tree.length
        ? this.tree.map((n) => this.renderNode(n, 0))
        : html`<cadence-empty-state heading=${t('events.todos.empty.heading')} message=${this.canManage ? t('events.todos.empty.manage') : t('common.states.empty')}></cadence-empty-state>`}
    `;
  }

  private renderNode(node: TodoNode, depth: number): unknown {
    const editing = this.editingId === node.id;
    return html`
      <div class="row" style="margin-left:${depth * 22}px"
        draggable=${this.canManage ? 'true' : 'false'}
        @dragstart=${() => (this.dragId = node.id)}
        @dragover=${(e: DragEvent) => { e.preventDefault(); (e.currentTarget as HTMLElement).classList.add('drag-over'); }}
        @dragleave=${(e: DragEvent) => (e.currentTarget as HTMLElement).classList.remove('drag-over')}
        @drop=${(e: DragEvent) => { e.preventDefault(); (e.currentTarget as HTMLElement).classList.remove('drag-over'); this.onDrop(node); }}>
        ${this.canManage ? html`<span class="grip" title=${t('events.todos.dragReorder')}>${icon('grip-vertical')}</span>` : ''}
        <input type="checkbox" .checked=${!!node.done} ?disabled=${!this.canManage}
          @change=${(e: Event) => this.patch(node.id, { done: (e.target as HTMLInputElement).checked })} />
        ${editing
          ? html`<div class="editwrap">
              <input class="edit" .value=${this.editText}
                @input=${(e: Event) => (this.editText = (e.target as HTMLInputElement).value)}
                @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.commitEdit(); if (e.key === 'Escape') this.cancelEdit(); }}
                @blur=${() => this.commitEdit()} autofocus />
              <button class="iconbtn confirm" title=${t('common.actions.save')} @mousedown=${(e: MouseEvent) => e.preventDefault()} @click=${() => this.commitEdit()}>${icon('check')}</button>
              <button class="iconbtn" title=${t('common.actions.cancel')} @mousedown=${(e: MouseEvent) => e.preventDefault()} @click=${() => this.cancelEdit()}>${icon('x')}</button>
            </div>`
          : html`<span class="text ${node.done ? 'done' : ''}" @click=${() => this.startEdit(node)}>${node.text}</span>`}

        ${this.canManage ? html`
          <select title=${t('events.todos.assignee')} @change=${(e: Event) => this.patch(node.id, { assignee_id: (e.target as HTMLSelectElement).value })}>
            <option value="" ?selected=${!node.assignee_id}>${t('events.todos.unassigned')}</option>
            ${this.staff.map((s) => html`<option value=${s.id} ?selected=${s.id === node.assignee_id}>${s.name}</option>`)}
          </select>
          <input type="date" title=${t('events.todos.dueDate')} .value=${node.due_date ?? ''}
            @change=${(e: Event) => this.patch(node.id, { due_date: (e.target as HTMLInputElement).value })} />
          <button class="iconbtn" title=${t('events.todos.addSubtask')} @click=${() => { this.addChildFor = node.id; this.childText = ''; }}>${icon('plus')}</button>
          <button class="iconbtn del" title=${t('events.actions.delete')} @click=${() => this.removeTodo(node)}>${icon('trash-2')}</button>
        ` : html`
          ${node.assignee_name ? html`<span class="meta">${node.assignee_name}</span>` : ''}
          ${this.fmtDue(node.due_date)}
        `}
      </div>

      ${this.addChildFor === node.id && this.canManage ? html`
        <div class="child-add" style="margin-left:${(depth + 1) * 22}px">
          <cadence-input placeholder=${t('events.todos.addSubtaskPlaceholder')} .value=${this.childText}
            @input=${(e: CustomEvent) => (this.childText = e.detail)}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.addTodo(node.id, this.childText); if (e.key === 'Escape') this.addChildFor = null; }}></cadence-input>
          <cadence-button size="sm" @click=${() => this.addTodo(node.id, this.childText)}>${t('events.actions.add')}</cadence-button>
          <cadence-button size="sm" variant="ghost" @click=${() => (this.addChildFor = null)}>${t('common.actions.cancel')}</cadence-button>
        </div>` : ''}

      ${node.children.map((ch) => this.renderNode(ch, depth + 1))}
    `;
  }
}
